import { BreedTypes } from "../../data/breeds";
import { log } from "../../engine/logger/logger";
import { SoundSystem } from "../../sounds";
import { DOWN_ARROW, VH, VW } from "../../tiles";
import { openModal } from "../../ui/containers/Modal";
import { createElement } from "../../ui/ui";
import { Action, createAction } from "../actions/Action";
import { wait } from "../actions/ActionMovement";
import { playWord } from "../actions/ActionWord";
import { hitPieces } from "../anims/hitAnimations";
import { InvalidWordParticle } from "../anims/particles/InvalidWordParticle";
import { SelectedWordParticle } from "../anims/particles/SelectedWordParticle";
import { ParticleManager } from "../anims/particleSystem";
import Colors from "../Colors";
import EntityManager from "../EntityManager";
import {
  findActorForPosition,
  gameData,
  getItemsForPosition,
} from "../GameManager";
import { FloorTypes } from "../map/Map";
import { SpellListModal } from "../ui/book/SpellList";
import { CheatModal } from "../ui/cheat/CheatModal";
import { addLetters } from "../words/letters";
import {
  getWordInfo,
  isCharAvailablePiece,
  piecePos,
} from "../words/wordUtils";
import { setFocusToCursor, setFocusToTarget } from "./focusUtils";

export const Commands: {
  [key: string]: (
    args?: any
  ) =>
    | void
    | Action<any>
    | Action<any>[]
    | Promise<void | Action<any> | Action<any>[]>;
} = {
  cheat: () => {
    openModal(createElement(CheatModal, { x: 0, y: 0, w: 40, h: 20 }));
  },
  spellList: () => {
    openModal(
      createElement(SpellListModal, { x: 0, w: VW - 2, y: 0, h: VH - 2 })
    );
  },
  wait: async () => {
    return createAction(wait, gameData.player, null);
  },
  moveTo: (pos: { x: number; y: number }) => {
    if (gameData.focus && gameData.current.inBounds(pos.x, pos.y)) {
      gameData.focus.x = pos.x;
      gameData.focus.y = pos.y;

      gameData.focusHighlights.forEach((p) => p.finish());
      const actor = findActorForPosition(gameData.focus.x, gameData.focus.y);
      if (actor) {
        gameData.focusHighlights = actor.pieces.map((p) =>
          ParticleManager.createParticle(
            SelectedWordParticle,
            p.x,
            p.y,
            p.breed.fg || Colors.WHITE
          )
        );
      } else {
        const items = getItemsForPosition(gameData.focus.x, gameData.focus.y);
        if (items.length) {
          gameData.focusHighlights = [
            ParticleManager.createParticle(
              SelectedWordParticle,
              gameData.focus.x,
              gameData.focus.y,
              items[0].breed.fg || Colors.WHITE
            ),
          ];
        }
      }

      if (gameData.target) {
        setFocusToTarget();
      }
    }
  },
  move: (direction: { x: number; y: number }) => {
    if (gameData.focus) {
      Commands.moveTo({
        x: gameData.focus.x + direction.x,
        y: gameData.focus.y + direction.y,
      });
    }
  },
  swapDirection: () => {
    const { x, y } = gameData.focusDirection;
    const ch = x ? DOWN_ARROW : ">";

    gameData.letters = [];
    gameData.focusDirection = { x: y, y: x };
    if (gameData.focus) {
      gameData.focus.char = ch;
      gameData.focus.initialChar = ch;
    }
  },
  submitWord: () => {
    if (!gameData.letters.length) {
      return;
    }

    const sorted = gameData.letters.sort((a, b) =>
      a.x < b.x || a.y < b.y ? -1 : 1
    );
    let direction = gameData.focusDirection;
    if (sorted.length > 1) {
      direction = sorted[0].x < sorted[1].x ? { x: 1, y: 0 } : { x: 0, y: 1 };

      for (const next of sorted) {
        if (
          (direction.x && next.y !== sorted[0].y) ||
          (direction.y && next.x !== sorted[0].x)
        ) {
          log("Tiles must be in a line");
          sorted.forEach((p) => {
            ParticleManager.runThenDestroy(
              InvalidWordParticle,
              p.x,
              p.y,
              p.breed.fg || Colors.WHITE
            );
          });

          return;
        }
      }
    }

    const wordInfo = getWordInfo(
      direction,
      sorted[0],
      sorted,
      gameData.player.id
    );
    if (
      wordInfo.valid &&
      wordInfo.lettersUsed === gameData.letters.length &&
      (gameData.freeEntry ||
        wordInfo.seenOwner ||
        wordInfo.crosses?.find((cross) => cross.seenOwner)) &&
      !wordInfo.failedCross
    ) {
      return createAction(playWord, gameData.player, {
        info: wordInfo,
        freeAction: gameData.freeEntry,
        noTriggers: gameData.noWordTriggers,
      });
    } else {
      if (wordInfo.failedCross) {
        wordInfo.crosses?.forEach((cross) => {
          if (!cross.valid && cross.pieces) {
            cross.pieces.forEach((p) => {
              ParticleManager.runThenDestroy(
                InvalidWordParticle,
                p.x,
                p.y,
                p.breed.fg || Colors.WHITE
              );
            });
          }
        });
        log("Word is not recognized");
      } else {
        if (wordInfo.pieces) {
          wordInfo.pieces.forEach((p) => {
            ParticleManager.runThenDestroy(
              InvalidWordParticle,
              p.x,
              p.y,
              p.breed.fg || Colors.WHITE
            );
          });
        }
        if (wordInfo.seenOwner) {
          log("Word is not recognized");
        } else {
          log("Must connect to player");
        }
      }
    }
  },
  letterInput: (ch: string) => {
    if (gameData.focus) {
      gameData.focusHighlights.forEach((p) => p.finish());
      gameData.focusHighlights = [];
      if (gameData.current.tile(gameData.focus).type !== FloorTypes.EMPTY) {
        return;
      }

      const existing = gameData.pieces.get(piecePos(gameData.focus));
      if (existing) {
        if (ch === existing.ch) {
          gameData.focus.x += gameData.focusDirection.x;
          gameData.focus.y += gameData.focusDirection.y;
          SoundSystem.play("move");
        }
      } else if (
        isCharAvailablePiece(ch, gameData.letters, gameData.player.spellWand)
      ) {
        const piece = EntityManager.createPiece(
          BreedTypes.letterPlacement,
          null,
          null,
          gameData.player.id,
          ch
        );
        piece.x = gameData.focus.x;
        piece.y = gameData.focus.y;
        gameData.letters.push(piece);

        let nextPieces = null;
        SoundSystem.play("move");
        do {
          gameData.focus.x += gameData.focusDirection.x;
          gameData.focus.y += gameData.focusDirection.y;
          nextPieces = gameData.pieces.get(piecePos(gameData.focus));
        } while (nextPieces != null);
      }
    }
  },
  backspace: () => {
    const last = gameData.letters.pop();
    if (last && gameData.focus) {
      gameData.focus.x = last.x;
      gameData.focus.y = last.y;
      SoundSystem.play("move");
    }
  },

  mouseTile: () => {
    console.log("mouseTileMove");
  },

  confirmTarget: () => {
    if (gameData.focus && gameData.target && gameData.targetCallback) {
      const error = gameData.target.validateTileReqs(
        gameData.player.pieces,
        gameData.focus
      );
      if (
        !error &&
        gameData.target.validateRange(gameData.player.pieces, gameData.focus)
      ) {
        gameData.targetCallback(gameData.focus);
      } else {
        log(error || "Target too far");
      }
    }
  },

  cancelTarget: () => {
    setFocusToCursor();
    if (gameData.targetCallback) {
      gameData.targetCallback();
    }
  },
};
